import React, { useState, useEffect } from "react";
import Context from "./context/context.js";
import { Auth } from "./components/Auth.js";
import WorkerApp from "./components/worker/WorkerApp";
import { AppLayout } from "./components/AppLayout";
import { AppLayout2 } from "./components/AppLayout2";
import { WilsonAppLayout } from "./components/WilsonAppLayout";
import { AppLayoutManuf } from "./components/AppLayoutManuf";
import Cookies from "universal-cookie";
import "./AppMuuzbox.scss";
import { AuthProvider } from "./context/AuthUserContext";
import { ToastProvider } from "./context/ToastContext";
import { TracksProvider } from "./context/TracksContext";
import { auth, db } from "./firebase-config";
import { doc, getDoc } from "firebase/firestore";
import AppleWatchModal from "./components/AppleWatchModal";
import AnonLinkPrompt from "./components/AnonLinkPrompt";
import { rememberPostLoginPanel, peekPostLoginChannel } from "./services/postLoginIntent";

// Capture a games deep-link (muuzbox.com/?panel=games#channel — e.g. a game
// night QR code) BEFORE any auth gating: a signed-out guest goes through the
// login screen first, and by the time AppLayoutManuf mounts the URL intent
// would otherwise be forgotten. AppLayoutManuf consumes this on mount and
// opens the Games panel instead of the default Jukebox.
try {
  if (new URLSearchParams(window.location.search).get("panel") === "games") {
    rememberPostLoginPanel("games", window.location.hash ? window.location.hash.substring(1) : null);
  }
} catch (_) {}

// Restore the venue after an Android sign-in handoff. Completing Google
// sign-in from a Chrome tab can relaunch the site as the INSTALLED PWA,
// whose start_url carries no #channel — the `client` state below would
// then boot as "default DJ" and the game night would have no venue. The
// pending games intent remembers the channel; re-apply it to the hash
// HERE, at module scope, so it exists before React reads it. Peek only —
// AppLayoutManuf still consumes the intent (when visible) to open Games.
try {
  if (!window.location.hash) {
    const pendingChannel = peekPostLoginChannel();
    if (pendingChannel) {
      window.location.hash = pendingChannel;
      console.log("📌 Restored venue channel from post-login intent:", pendingChannel);
    }
  }
} catch (_) {}

const cookies = new Cookies();

// Mood image URLs to preload
const MOOD_IMAGE_URLS = [
  "https://firebasestorage.googleapis.com/v0/b/chatbout001.appspot.com/o/images%2Fparty-mood.png?alt=media&token=890ef763-5261-4a75-b924-0438109a48ed",
  "https://firebasestorage.googleapis.com/v0/b/chatbout001.appspot.com/o/images%2Fchill-mood.png?alt=media&token=034a14ff-1e13-4804-aa01-c5875f07d22b",
  "https://firebasestorage.googleapis.com/v0/b/chatbout001.appspot.com/o/images%2Fenergetic-mood.png?alt=media&token=083fa7f1-b111-4efe-8ea1-c0e993a1aac5",
  "https://firebasestorage.googleapis.com/v0/b/chatbout001.appspot.com/o/images%2Fromantic-mood.png?alt=media&token=50ae15b8-054e-4908-ac29-c8d6231af77b"
];

function ChatApp({ items }) {
  const [client, setClient] = useState(() => {
    if (window.location.hash) {
      const hash = window.location.hash.substring(1); //Puts hash in variable, and removes the # character
      console.log("hash to look up for clients:", hash);
      // hash found
      return hash;
    } else {
      // No hash found
      console.log("load the default DJ for muuzbox");
      return null;
    }
  });
  const [isAuth, setIsAuth] = useState(() => {
    if (window.location.hostname === "admin.muuzbox.com") {
      return false;
    } else {
      return cookies.get("auth-token");
    }
  });
  const [isInChat, setIsInChat] = useState(null);
  const [chatId, setChatId] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const [isLaunched, setIsLaunched] = useState(false);
  const [layoutSwitch, setLayoutSwitch] = useState(false);
  const [layoutIndex, setLayoutIndex] = useState(0);
  const layouts = ["Two", "Three", "Four"];
  const [appMode, setAppMode] = useState(() => {
    if (window.location.hostname === "admin.muuzbox.com") {
      setLayoutIndex(0);
      return { login: "true" };
    } else {
      return null;
    }
  });
  const [pendingChatNavigation, setPendingChatNavigation] = useState(null);

  // Apple Watch Dock modal state - show every time app loads
  const [showAppleWatchModal, setShowAppleWatchModal] = useState(false);
  const [navigateToPlaying, setNavigateToPlaying] = useState(false);

  // Cached user selections for AppleWatchDock (preloaded before modal opens)
  const [cachedUserSelections, setCachedUserSelections] = useState(null);

  // Preload mood images when app loads
  useEffect(() => {
    MOOD_IMAGE_URLS.forEach(url => {
      const img = new Image();
      img.src = url;
    });
  }, []);

  // Show modal when auth state is confirmed and hide loading
  // BYPASSED: App now goes straight to Request page (Jukebox4)
  // useEffect(() => {
  //   if (isAuth) {
  //     setShowAppleWatchModal(true);
  //     // Hide loading after a brief delay to ensure modal is ready
  //     const timer = setTimeout(() => {
  //       setIsAppLoading(false);
  //     }, 100);
  //     return () => clearTimeout(timer);
  //   }
  // }, [isAuth]);

  // Preload user selections when auth.currentUser is available
  useEffect(() => {
    if (isAuth && auth.currentUser) {
      // Preload user selections from Firestore
      getDoc(doc(db, "users", auth.currentUser.uid))
        .then(userDoc => {
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setCachedUserSelections({
              moods: userData.selectedMoods || [],
              genres: userData.selectedGenres || []
            });
          } else {
            setCachedUserSelections({ moods: [], genres: [] });
          }
        })
        .catch(error => {
          console.error("Error preloading user selections:", error);
          setCachedUserSelections({ moods: [], genres: [] });
        });
    }
  }, [isAuth]);

  // Handler for navigating to playing tab after modal close
  const handleNavigateToPlaying = () => {
    setNavigateToPlaying(true);
  };

  useEffect(() => {}, [appMode]);

  // Memory monitoring (development only)
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;

    if (!performance.memory) {
      console.log('[MEMORY] Performance.memory API not available in this browser');
      return;
    }

    console.log('[MEMORY] Memory monitoring active');

    const interval = setInterval(() => {
      const used = (performance.memory.usedJSHeapSize / 1048576).toFixed(2);
      const total = (performance.memory.totalJSHeapSize / 1048576).toFixed(2);
      const limit = (performance.memory.jsHeapSizeLimit / 1048576).toFixed(2);

      console.log(`[MEMORY] Used: ${used}MB | Total: ${total}MB | Limit: ${limit}MB`);

      // Warning threshold: 200MB
      if (performance.memory.usedJSHeapSize > 200 * 1048576) {
        console.warn('⚠️ [MEMORY] High memory usage detected! Possible memory leak.');
      }

      // Critical threshold: 400MB
      if (performance.memory.usedJSHeapSize > 400 * 1048576) {
        console.error('🔴 [MEMORY] CRITICAL! Memory usage very high! Memory leak likely.');
      }
    }, 10000); // Log every 10 seconds

    return () => clearInterval(interval);
  }, []);

  const appLayoutMode = (mode) => {
    setLayoutIndex((layoutIndex + 1) % layouts.length);

    console.log("*alm", mode);
    console.log("layoutIndex", layoutIndex);
    setLayoutSwitch(!layoutSwitch);
  };

  // Handle toast click navigation
  const handleToastClick = (toast) => {
    if (toast.chatId && toast.users) {
      // Store the navigation data for the layout components to handle
      setPendingChatNavigation({
        chatId: toast.chatId,
        chatType: toast.chatType,
        users: toast.users
      });

      // Ensure we're in chat mode
      setIsInChat(true);
      setChatId(toast.chatId);
    }
  };


  // Worker mode — accessed via #worker (or #/worker). Standalone, doesn't
  // depend on the rest of the app. Auth is handled inside WorkerApp itself.
  const hashRoute = (window.location.hash || '').replace(/^#\/?/, '').toLowerCase();
  if (hashRoute === 'worker') {
    return (
      <AuthProvider>
        <WorkerApp />
      </AuthProvider>
    );
  }

  if (!isAuth) {
    return (
      <AuthProvider>
        <ToastProvider onToastClick={handleToastClick}>
          <Context>
            <AppLayout
              isAuth={isAuth}
              setIsAuth={setIsAuth}
              setIsInChat={setIsInChat}
              setIsLaunched={setIsLaunched}
              appMode={appMode}
              client={client}
              pendingChatNavigation={pendingChatNavigation}
              setPendingChatNavigation={setPendingChatNavigation}
            >
              <div
                className="bg-black bg-opacity-80 app-container max-w-full"
                x="bg-black bg-opacity-80 h-screen w-screen top-0 left-0 fixed z-10"
              >
                <div className="h-screen flex items-center justify-center">
                  <Auth
                    setIsAuth={setIsAuth}
                    appMode={appMode}
                    appLaunched={isLaunched}
                    client={client}
                  />
                </div>
              </div>
            </AppLayout>
          </Context>
        </ToastProvider>
      </AuthProvider>
    );
  }

  // if (layouts[layoutIndex] === "One") {
  //   return (
  //     <AuthProvider>
  //       <Context>
  //         <AppLayoutManuf
  //           setIsLaunched={setIsLaunched}
  //           isAuth={isAuth}
  //           setIsAuth={setIsAuth}
  //           setIsInChat={setIsInChat}
  //           setChatId={setChatId}
  //           appMode={appMode}
  //           client={client}
  //           appLayoutMode={appLayoutMode}
  //         ></AppLayoutManuf>
  //       </Context>
  //     </AuthProvider>
  //   );
  // }

  if (layouts[layoutIndex] === "Two") {
    return (
      <AuthProvider>
        <ToastProvider onToastClick={handleToastClick}>
          <TracksProvider>
            <Context>
              <AppleWatchModal
                isOpen={showAppleWatchModal}
                onClose={() => setShowAppleWatchModal(false)}
                onNavigateToPlaying={handleNavigateToPlaying}
                cachedSelections={cachedUserSelections}
              />

              {/* Listens for the muuzbox:show-anon-link-prompt event fired
                  after an anonymous guest's 2nd song request and shows the
                  "Save your request history → Continue with Google" sheet. */}
              <AnonLinkPrompt />

              <AppLayoutManuf
              setIsLaunched={setIsLaunched}
              isAuth={isAuth}
              setIsAuth={setIsAuth}
              setIsInChat={setIsInChat}
              setChatId={setChatId}
              appMode={appMode}
              client={client}
              appLayoutMode={appLayoutMode}
              pendingChatNavigation={pendingChatNavigation}
              setPendingChatNavigation={setPendingChatNavigation}
              navigateToPlaying={navigateToPlaying}
              setNavigateToPlaying={setNavigateToPlaying}
            ></AppLayoutManuf>
          </Context>
          </TracksProvider>
        </ToastProvider>
      </AuthProvider>
    );
  }

  if (layouts[layoutIndex] === "Four") {
    return (
      <AuthProvider>
        <ToastProvider onToastClick={handleToastClick}>
          <TracksProvider>
            <Context>
              <AppleWatchModal
                isOpen={showAppleWatchModal}
                onClose={() => setShowAppleWatchModal(false)}
                onNavigateToPlaying={handleNavigateToPlaying}
                cachedSelections={cachedUserSelections}
              />

              <AppLayout
              setIsLaunched={setIsLaunched}
              isAuth={isAuth}
              setIsAuth={setIsAuth}
              setIsInChat={setIsInChat}
              setChatId={setChatId}
              appMode={appMode}
              client={client}
              appLayoutMode={appLayoutMode}
              pendingChatNavigation={pendingChatNavigation}
              setPendingChatNavigation={setPendingChatNavigation}
            ></AppLayout>
          </Context>
          </TracksProvider>
        </ToastProvider>
      </AuthProvider>
    );
  }

  return (
    <AuthProvider>
      <ToastProvider onToastClick={handleToastClick}>
        <TracksProvider>
          <Context>
            <AppleWatchModal
              isOpen={showAppleWatchModal}
              onClose={() => setShowAppleWatchModal(false)}
              onNavigateToPlaying={handleNavigateToPlaying}
              cachedSelections={cachedUserSelections}
            />

            <AppLayout2
            setIsLaunched={setIsLaunched}
            isAuth={isAuth}
            setIsAuth={setIsAuth}
            setIsInChat={setIsInChat}
            setChatId={setChatId}
            appMode={appMode}
            client={client}
            appLayoutMode={appLayoutMode}
            pendingChatNavigation={pendingChatNavigation}
            setPendingChatNavigation={setPendingChatNavigation}
          ></AppLayout2>
        </Context>
        </TracksProvider>
      </ToastProvider>
    </AuthProvider>
  );
}

export default ChatApp;
